/**
 * The level-clear bonus (spec 02 §7.6), ported from rebb64: the level-clear sweep ($171B) turns every
 * plain bubble into 700-point food (type $4C) when $68 is set - on the scheduled levels (player-state.s
 * $067F) or after a staff or box (bonus-round.s: D_348A..D_350A) - and, with a staff or box (ARG set),
 * D_1E2E clears the monster sprites and the giant item drops (sprite state 21, D_2ACB). The food falls
 * and is taken in the entity pass (L0EC5), the giant on I-ticks.
 */

import { addScore } from '../score';
import { BURST_3, FULL, GONE } from './codes';
import { bySlot, colOf, drawnY, foodFallOf, freeBubble, mapSolid, type FoodItem } from './slots';
import {
  BUBBLE_FOOD_END_ROW, BUBBLE_FOOD_RANGE, BUBBLE_FOOD_UNITS, GIANT_BONUS, GIANT_H, GIANT_LAND_Y, GIANT_STEP,
  GIANT_W, GIANT_X, scheduledFood,
} from './tuning';
import type { Item, Player, World } from '../types';

/**
 * The flow's call as the level clears (02 §7.6). $68 is the scheduled food, or the staff's or box's -
 * and a staff or box always sets it, so its clear turns the bubbles into food as well.
 */
export function clearBonus(world: World, bonus: { bubbleFood: boolean; giant: number }): void {
  const giant = GIANT_BONUS[bonus.giant];
  if (!bonus.bubbleFood && !giant) return;
  toFood(world, giant ? giant.food : scheduledFood(world.levelIndex));
  if (giant) dropGiant(world, bonus.giant, giant.units);
}

/**
 * $171B..$174D: a plain full bubble ($04), or one bursting or vanishing ($38..$41), becomes food in its
 * slot, its cell and sub-row kept (the blink's hidden half, $48, bursts like the rest). Drawn at its
 * column (routine_3F88 has one picture, no sub-column shift) and row.
 */
function toFood(world: World, food: number): void {
  for (const b of bySlot(world)) {
    const c = b.motion.code;
    if (c !== FULL && (c < GONE || c > BURST_3 + 1)) continue;
    freeBubble(world, b);
    const m = b.motion;
    const item: FoodItem = {
      id: world.nextId++, kind: 'bubbleFood', item: food, x: (colOf(b) * 8 + 0x14) & 0xff, y: drawnY(m.row, m.subY),
      value: BUBBLE_FOOD_UNITS, life: 0, falling: true, fall: { slot: m.slot, row: m.row, subY: m.subY },
    };
    world.items.push(item);
  }
}

/**
 * L0EC5, one pass of a food: a sub-row is first cleared; else, with the tile row under it (row - 1,
 * its column and the next) open, it drops a row with the sub-row 4 - 4 px a pass - and is gone at row
 * $1D. Then a player (2 first, any state but out) within 16 px of its cell (col x 8 + $14, row x 8 +
 * $15) takes it: 700.
 */
export function foodStep(world: World, it: FoodItem): void {
  const f = it.fall;
  const col = ((it.x - 0x14) & 0xff) >> 3;
  if (f.subY !== 0) {
    f.subY = 0;
  } else if (mapSolid(world, col, f.row - 1) || mapSolid(world, col + 1, f.row - 1)) {
    it.falling = false;
  } else {
    f.row++;
    if (f.row === BUBBLE_FOOD_END_ROW) return remove(world, it);
    f.subY = 4;
  }
  it.y = drawnY(f.row, f.subY);
  const fy = (f.row * 8 + 0x15) & 0xff;
  const taker = takerOf(world, (p) => Math.abs(p.x - it.x) < BUBBLE_FOOD_RANGE && Math.abs(p.y - fy) < BUBBLE_FOOD_RANGE);
  if (taker) collect(world, it, taker);
}

/** The bubble foods in the world (the entity pass runs them by slot with the bubbles). */
export const foodItems = (world: World): FoodItem[] =>
  world.items.filter((i): i is FoodItem => foodFallOf(i) !== null);

/**
 * D_1E2E: the six monster sprites are cleared - every death item and dying monster's flight goes - and
 * the giant's four sprites start: its top-left at X $80, Y 0 (the bottom row at Y $15, $1756).
 */
function dropGiant(world: World, index: number, units: number): void {
  for (const it of world.items.filter((i) => i.kind === 'death')) remove(world, it);
  world.items.push({ id: world.nextId++, kind: 'giant', item: index, x: GIANT_X, y: 0, value: units, life: 0, falling: true });
}

/**
 * D_2ACB, every I-tick: down 4 px until its top reaches Y $C8 (50 I-ticks: 2 s), raising `item` as it
 * lands; from the next tick a player (2 first) with 0 <= X - $80 < $30 and 0 <= Y - $C8 < $24 takes it,
 * for the staff's or box's value.
 */
export function giantTick(world: World): void {
  for (const it of world.items.filter((i) => i.kind === 'giant')) {
    if (it.falling) {
      it.y = (it.y + GIANT_STEP) & 0xff;
      if (it.y !== GIANT_LAND_Y) continue;
      it.falling = false;
      world.events.push({ type: 'item', itemId: it.id, spawned: true });
      continue;
    }
    const taker = takerOf(world, (p) => ((p.x - it.x) & 0xff) < GIANT_W && ((p.y - it.y) & 0xff) < GIANT_H);
    if (taker) collect(world, it, taker);
  }
}

/** Player 2, then 1, in play or dying (the C64 tests only that the player exists), meeting `near`. */
function takerOf(world: World, near: (p: Player) => boolean): Player | null {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if ((p.state === 'play' || p.state === 'dying') && near(p)) return p;
  }
  return null;
}

function collect(world: World, it: Item, p: Player): void {
  addScore(world, p.id, it.value);
  world.events.push({ type: 'collect', player: p.id, itemId: it.id, value: it.value });
  remove(world, it);
}

function remove(world: World, it: Item): void {
  const i = world.items.indexOf(it);
  if (i >= 0) world.items.splice(i, 1);
}
